import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { config } from '../../config';
import { getValidToken } from '../../utils/tokenRefresh';
import './OrganizationManagement.css';

interface Organization {
  id: string;
  name: string;
  organization_type: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  billing_email?: string;
  billing_address?: string;
  tax_id?: string;
  status: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

const OrganizationManagement: React.FC = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    organization_type: 'club',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    city: '',
    province: '',
    postal_code: '',
    billing_email: '',
    billing_address: '',
    tax_id: '',
    status: 'active',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${config.BACKEND_URL}/api/organizations/`, { headers });
      setOrganizations(response.data || []);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching organizations:', err);
      setError(err.response?.data?.detail || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setSelectedOrganization(null);
    setIsEditing(false);
    setFormData({
      name: '',
      organization_type: 'club',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      city: '',
      province: '',
      postal_code: '',
      billing_email: '',
      billing_address: '',
      tax_id: '',
      status: 'active',
      notes: ''
    });
    setFormErrors({});
    setShowForm(true);
  };

  const handleEdit = (org: Organization) => {
    setSelectedOrganization(org);
    setIsEditing(true);
    setFormData({
      name: org.name,
      organization_type: org.organization_type,
      contact_name: org.contact_name || '',
      contact_email: org.contact_email || '',
      contact_phone: org.contact_phone || '',
      address: org.address || '',
      city: org.city || '',
      province: org.province || '',
      postal_code: org.postal_code || '',
      billing_email: org.billing_email || '',
      billing_address: org.billing_address || '',
      tax_id: org.tax_id || '',
      status: org.status,
      notes: org.notes || ''
    });
    setFormErrors({});
    setShowForm(true);
  };

  const handleDelete = async (orgId: string, orgName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${orgName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.delete(`${config.BACKEND_URL}/api/organizations/${orgId}`, { headers });
      alert('Organization deleted successfully');
      fetchOrganizations();
    } catch (err: any) {
      console.error('Error deleting organization:', err);
      alert(err.response?.data?.detail || 'Failed to delete organization');
    }
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Organization name is required';
    }
    
    if (!formData.organization_type) {
      errors.organization_type = 'Organization type is required';
    }

    if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      errors.contact_email = 'Invalid email format';
    }

    if (formData.billing_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.billing_email)) {
      errors.billing_email = 'Invalid email format';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Clean up form data - convert empty strings to null for optional fields
      const cleanedFormData = {
        ...formData,
        contact_name: formData.contact_name || null,
        contact_email: formData.contact_email || null,
        contact_phone: formData.contact_phone || null,
        address: formData.address || null,
        city: formData.city || null,
        province: formData.province || null,
        postal_code: formData.postal_code || null,
        billing_email: formData.billing_email || null,
        billing_address: formData.billing_address || null,
        tax_id: formData.tax_id || null,
        notes: formData.notes || null
      };
      
      if (isEditing && selectedOrganization) {
        await axios.put(
          `${config.BACKEND_URL}/api/organizations/${selectedOrganization.id}`,
          cleanedFormData,
          { headers }
        );
        alert('Organization updated successfully');
      } else {
        await axios.post(
          `${config.BACKEND_URL}/api/organizations/`,
          cleanedFormData,
          { headers }
        );
        alert('Organization created successfully');
      }
      
      setShowForm(false);
      fetchOrganizations();
    } catch (err: any) {
      console.error('Error saving organization:', err);
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to save organization';
      if (err.response?.data?.detail && typeof err.response.data.detail === 'object') {
        // Handle validation errors
        const validationErrors = err.response.data.detail;
        const errorList = Object.entries(validationErrors).map(([field, errors]: [string, any]) => {
          const errorMsg = Array.isArray(errors) ? errors.join(', ') : errors;
          return `${field}: ${errorMsg}`;
        }).join('\n');
        alert(`Validation Error:\n${errorList}`);
      } else {
        alert(errorMessage);
      }
    }
  };

  const getOrganizationTypeLabel = (type: string) => {
    const labels: {[key: string]: string} = {
      'club': 'Club',
      'scouts': 'Scouts',
      'guides': 'Girl Guides',
      'school_district': 'School District',
      'online_school': 'Online School',
      'other': 'Other'
    };
    return labels[type] || type;
  };

  if (loading) {
    return <div className="loading">Loading organizations...</div>;
  }

  return (
    <div className="organization-management">
      <div className="orgs-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3>Organization Management</h3>
        <button className="btn btn-primary" onClick={handleAddNew}>
          ➕ Add Organization
        </button>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px' }}>
          {error}
        </div>
      )}

      {organizations.length === 0 ? (
        <div className="empty-state">
          <p>No organizations found. Click "Add Organization" to create one.</p>
        </div>
      ) : (
        <div className="organizations-table-container">
          <table className="organizations-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Contact Name</th>
                <th>Contact Email</th>
                <th>Contact Phone</th>
                <th>City</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {organizations.map((org) => (
                <tr key={org.id}>
                  <td>{org.name}</td>
                  <td>{getOrganizationTypeLabel(org.organization_type)}</td>
                  <td>{org.contact_name || '-'}</td>
                  <td>{org.contact_email || '-'}</td>
                  <td>{org.contact_phone || '-'}</td>
                  <td>{org.city || '-'}</td>
                  <td>
                    <span className={`status-badge status-${org.status}`}>
                      {org.status}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleEdit(org)}
                      style={{ marginRight: '8px' }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(org.id, org.name)}
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Organization Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content org-form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{isEditing ? 'Edit Organization' : 'Add New Organization'}</h2>
              <button className="modal-close" onClick={() => setShowForm(false)}>×</button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '30px' }}>
              <div className="form-row">
                <div className="form-group">
                  <label>Organization Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                  {formErrors.name && <span className="error-text">{formErrors.name}</span>}
                </div>

                <div className="form-group">
                  <label>Organization Type *</label>
                  <select
                    value={formData.organization_type}
                    onChange={(e) => setFormData({ ...formData, organization_type: e.target.value })}
                    required
                  >
                    <option value="club">Club</option>
                    <option value="scouts">Scouts</option>
                    <option value="guides">Girl Guides</option>
                    <option value="school_district">School District</option>
                    <option value="online_school">Online School</option>
                    <option value="other">Other</option>
                  </select>
                  {formErrors.organization_type && <span className="error-text">{formErrors.organization_type}</span>}
                </div>
              </div>

              <div className="form-section-header">
                <h4>Contact Information</h4>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Contact Name</label>
                  <input
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Contact Email</label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  />
                  {formErrors.contact_email && <span className="error-text">{formErrors.contact_email}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Contact Phone</label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>

              <div className="form-section-header">
                <h4>Address</h4>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label>Street Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Province</label>
                  <input
                    type="text"
                    value={formData.province}
                    onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                    placeholder="e.g., BC"
                  />
                </div>

                <div className="form-group">
                  <label>Postal Code</label>
                  <input
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-section-header">
                <h4>Billing Information</h4>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Billing Email</label>
                  <input
                    type="email"
                    value={formData.billing_email}
                    onChange={(e) => setFormData({ ...formData, billing_email: e.target.value })}
                    placeholder="Email for invoice delivery"
                  />
                  {formErrors.billing_email && <span className="error-text">{formErrors.billing_email}</span>}
                </div>

                <div className="form-group">
                  <label>Tax ID / Business Number</label>
                  <input
                    type="text"
                    value={formData.tax_id}
                    onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label>Billing Address</label>
                  <textarea
                    value={formData.billing_address}
                    onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
                    rows={3}
                    placeholder="Separate billing address if different from main address"
                  />
                </div>
              </div>

              <div className="form-section-header">
                <h4>Additional Information</h4>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label>Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={4}
                    placeholder="Additional notes about this organization"
                  />
                </div>
              </div>

              <div className="modal-actions" style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  {isEditing ? 'Update Organization' : 'Create Organization'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationManagement;

